import {createContext, useState} from 'react';
import axios, {AxiosError} from 'axios';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useNavigate} from 'react-router-dom';
import {IDiscountCode} from '../models/IDiscountCode.ts';

const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL + '/api/discountCodes';

interface IDiscountCodeContextProps {
    discountCodes: IDiscountCode[] | undefined;
    createDiscountCodeMutation: any;
    createError: AxiosError | null;
    validateCode: (code: string) => Promise<{ valid: boolean; discountPercent?: number; message?: string }>;
    markCodeUsed: (code: string) => Promise<{ success: boolean; message: string }>;
}

const DiscountCodeContext = createContext<IDiscountCodeContextProps>({} as IDiscountCodeContextProps);

export const DiscountCodeProvider = ({children}: { children: React.ReactNode }) => {
    const [createError, setCreateError] = useState<AxiosError | null>(null);
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const {data: discountCodes} = useQuery<IDiscountCode[]>({
        queryKey: ['discountCodes'],
        queryFn: async () => {
            const res = await axios.get(`${VITE_API_BASE_URL}`, {withCredentials: true});
            return res.data;
        },
        retry: false,
    });

    const createDiscountCodeMutation = useMutation({
        mutationFn: async (code: IDiscountCode) => {
            return axios.post(`${VITE_API_BASE_URL}`, code, {withCredentials: true});
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['discountCodes']});
            navigate('/profile');
        },
        onError: (err: AxiosError) => {
            setCreateError(err);
        },
    });

    const validateCode = async (code: string) => {
        try {
            const res = await axios.get(`${VITE_API_BASE_URL}/validate?code=${code}`);
            return res.data;
        } catch (err: any) {
            return err.message || {valid: false, message: 'Unknown error'};
        }
    };

    const markCodeUsed = async (code: string) => {
        try {
            await axios.post(`${VITE_API_BASE_URL}/mark-used`, {code}, {withCredentials: true});
            return {success: true, message: 'Code marked as used'};
        } catch (err: any) {
            return {success: false, message: err.message || 'Error marking code used'};
        }
    };

    return (
        <DiscountCodeContext.Provider
            value={{
                discountCodes,
                createDiscountCodeMutation,
                createError,
                validateCode,
                markCodeUsed,
            }}
        >
            {children}
        </DiscountCodeContext.Provider>
    );
};

export default DiscountCodeContext;
